import {
  AbsenceReasonCategory,
  AbsenceResponse,
  AbsenceState,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { TagColorEnum } from '../../Tags/Tags';

/**
 * Single source of truth for the colour of an absence tag, shared by the
 * absences module (student detail page, calendar popover, class drawer) and the
 * classbook (attendance card and "edit attendance" dialog) so the same entry can
 * never read differently in two places (Bug #11405).
 *
 * The excuse definition — not `AbsenceState` — decides whether an absence counts
 * as excused: a reason only becomes excused by an excuse definition whose
 * `isCountingAsExcused` is set.
 *
 * @param absence The absence, attendance or unchecked entry to colour.
 * @param isInfoText Set for the "excluded from statistics" hint, which is always
 * yellow regardless of the absence itself.
 * @returns The tag colour to render.
 */
export const getAbsenceTagColor = (
  absence: AbsenceResponse,
  isInfoText?: boolean,
): TagColorEnum => {
  if (!absence?.absenceReason?.includeInStatistics && isInfoText) {
    return TagColorEnum.Yellow;
  }

  if (absence?.state === AbsenceState.present) {
    return TagColorEnum.Green;
  }

  // An unchecked entry carries no statement about the student at all, so it stays
  // neutral even when a reason happens to be attached.
  if (absence?.state === AbsenceState.unchecked) {
    return TagColorEnum.Grey;
  }

  const definition = absence?.absenceReason?.definition;

  if (definition) {
    return definition.isCountingAsExcused ? TagColorEnum.Green : TagColorEnum.Red;
  }

  const category = absence?.absenceReason?.category;

  // Partial absences (late arrival / left early) carry no excuse definition yet.
  // Red would read as "unexcused absence" although the student was present for
  // the rest of the lesson — they get the teal of the LATE_OR_LEFT_EARLY
  // indicator of the class view instead.
  if (category === AbsenceReasonCategory.late || category === AbsenceReasonCategory.leftEarly) {
    return TagColorEnum.Teal;
  }

  return TagColorEnum.Red;
};
